import CheckIcon from '@mui/icons-material/Check'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import DialogContent from '@mui/material/DialogContent'
import Typography from '@mui/material/Typography'
import React from 'react'

import type { SchemaQuota } from '@/__generated/es-api'
import Button from '@/app/component/atom/button'
import Dialog from '@/app/component/atom/dialog'
import DialogHeader from '@/app/component/atom/dialog/header'
import ErrorLayout from '@/app/component/layout/error'
import { useGetQuotas } from '@/app/module/content/hooks/use-get-quotas'

type Props = {
  addedQuotaIds: string[]
  onAddQuota: (quota: SchemaQuota) => void
  onClose: () => void
}

const formatCampaignCount = (campaigns: number): string =>
  `Used in ${campaigns} ${campaigns === 1 ? 'campaign' : 'campaigns'}`

const QuotaLibraryDialog: React.FC<Props> = ({ addedQuotaIds, onAddQuota, onClose }) => {
  const { data, error, isFetching, refetch } = useGetQuotas()
  const errorStatus = error && 'status' in error && typeof error.status === 'number' ? error.status : undefined
  const quotas = data?.quotas ?? []

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open
      slotProps={{ paper: { sx: { maxHeight: 'min(640px, calc(100% - 64px))' } } }}
      onClose={onClose}
    >
      <DialogHeader
        closeButtonLabel="Close organization quota dialog"
        title="Add a quota from your account"
        onClose={onClose}
      />
      <DialogContent sx={{ minHeight: 180 }}>
        {isFetching && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress aria-label="Loading organization quotas" />
          </Box>
        )}
        {!isFetching && !!error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <ErrorLayout status={errorStatus}>
              <Typography align="center">Unable to load organization quotas.</Typography>
              <Button sx={{ mt: 2 }} variant="contained" onClick={() => refetch()}>
                Try again
              </Button>
            </ErrorLayout>
          </Box>
        )}
        {!isFetching && !error && quotas.length === 0 && (
          <Typography align="center" color="text.secondary" sx={{ py: 6 }}>
            No quotas available.
          </Typography>
        )}
        {!isFetching && !error && quotas.length > 0 && (
          <>
            <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
              Adding a quota reuses its <strong>name and goal</strong> across campaigns. You&apos;ll set the conditions
              for this campaign after adding.
            </Typography>
            {quotas.map((quota) => {
              const isAdded = addedQuotaIds.includes(quota.id)

              return (
                <Box
                  key={quota.id}
                  sx={{
                    alignItems: 'center',
                    bgcolor: 'action.hover',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    display: 'flex',
                    gap: 2,
                    mb: 1.25,
                    px: 2,
                    py: 1.75,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Typography data-testid="organization-quota-name" fontWeight={600} variant="body1">
                        {quota.name}
                      </Typography>
                      <Chip label={formatCampaignCount(quota.campaigns)} size="small" />
                    </Box>
                    <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="body2">
                      Goal: {quota.goal} responses
                    </Typography>
                  </Box>
                  {isAdded ? (
                    <Typography
                      sx={{ alignItems: 'center', display: 'flex', flexShrink: 0, gap: 0.75 }}
                      variant="body2"
                    >
                      <CheckIcon fontSize="small" /> Already added
                    </Typography>
                  ) : (
                    <Button
                      aria-label={`Add & Configure ${quota.name}`}
                      variant="contained"
                      onClick={() => onAddQuota(quota)}
                    >
                      Add &amp; Configure
                    </Button>
                  )}
                </Box>
              )
            })}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default QuotaLibraryDialog
